import { centsToFormattedDollars } from '@fruitful/fruitils';
import { useAtomValue } from 'jotai';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Platform } from 'react-native';
import Animated, {
    FadeIn,
    FadeOut,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated';
import {
    AlertTriangleIcon,
    BadgeLabel,
    BodyMediumRegular,
    BodySmallRegular,
    CircleCheckIcon,
    FruitfulCustomIcon,
    Icon,
    InfoIcon,
    InstitutionLogo,
    Maximize2Icon,
    Minimize2Icon,
    TooltipTrigger,
    useThemeColors,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { selectedIncomeSplitExecutionAtom } from '../state/IncomeSplitAtoms';
import {
    type IncomeSplitRuleWithMoneyMapRule,
    PaycheckRoutingRuleAccountType,
} from '../types/IncomeSplit';
import {
    type IncomeSplitExecution,
    IncomeSplitOverviewColors,
    type IncomeSplitOverviewData,
    type IncomeSplitTransfer,
    type IncomeSplitTransferWithRuleAndHistory,
} from '../types/IncomeSplitOverviewTypes';

const PROGRESS_BAR_ENTERING_DURATION = 400;
const PROGRESS_BAR_GAP = 2;

const PROGRESS_BAR_BORDER_RADIUS = 4;

const upper = (value: string | undefined): string | undefined =>
    value?.toUpperCase();

const getExternalInstitutionLogo = (
    logo: string | null | undefined,
    imgBase64: string | null | undefined,
): string | undefined => {
    return logo ?? imgBase64 ?? undefined;
};

const getLastFour = (accountNumber: string | null | undefined) => {
    const trimmedAccountNumber = accountNumber?.trim();
    if (trimmedAccountNumber == null || trimmedAccountNumber.length === 0) {
        return null;
    }

    return trimmedAccountNumber.slice(-4);
};

const formatAccountNameWithLastFour = (
    accountName: string,
    accountNumber: string | null | undefined,
) => {
    const lastFour = getLastFour(accountNumber);
    return lastFour == null ? accountName : `${accountName} (${lastFour})`;
};

const mappedStatusMeansPending = (mapped: string | undefined): boolean =>
    mapped === 'PROCESSING' || mapped === 'PENDING';

const isInternalInvestmentTransferPending = (
    transfer: IncomeSplitTransfer,
): boolean => {
    const mapped = upper(transfer.mappedStatus);
    if (mapped != null && mapped.length > 0) {
        return mappedStatusMeansPending(mapped);
    }
    return upper(transfer.status) === 'PROCESSING';
};

const progressSegmentEntering = (targetValues: { targetWidth: number }) => {
    'worklet';
    return {
        initialValues: { width: 0 },
        animations: {
            width: withTiming(targetValues.targetWidth, {
                duration: PROGRESS_BAR_ENTERING_DURATION,
            }),
        },
    };
};

type GoalCompletionKind =
    | 'target_balance'
    | 'monthly_contribution'
    | 'annual_contribution';

interface ExecutionRuleSetTransfer extends IncomeSplitTransferWithRuleAndHistory {
    goalCompletedAt?: string | null;
    goalCompletionKind?: GoalCompletionKind | null;
}

interface RuleSetTransfersResult {
    name: string;
    transfers: ExecutionRuleSetTransfer[];
}

const getProgress = (
    transferAmount: number,
    max: number | undefined,
    previousTransfers: IncomeSplitTransfer[],
): Progress => {
    return max != null
        ? {
              total:
                  (previousTransfers.reduce(
                      (acc, prevTransfer) => acc + prevTransfer.amount,
                      0,
                  ) +
                      transferAmount) /
                  max,
              current: transferAmount / max,
          }
        : { total: 0, current: 0 };
};

interface Progress {
    total: number;
    current: number;
}

interface HighlightableAttribute {
    key: string;
    previousValue: number;
    currentValue: number;
    limit: number;
    monthlyProgress?: Progress;
    yearlyProgress?: Progress;
    retirementProgress?: Progress;
    targetProgress?: Progress;
}

const getAttributeToHighlight = (
    monthlyProgress: Progress,
    monthlyContributionMaxCents: number | undefined,
    yearlyProgress: Progress,
    yearlyContributionMaxCents: number | undefined,
    retirementProgress: Progress,
    retirementContributionLimit: number | undefined,
    targetProgress: Progress,
    targetBalanceCents: number | undefined,
): HighlightableAttribute | undefined => {
    const attributesToHighlight: HighlightableAttribute[] = [];

    if (monthlyContributionMaxCents != null) {
        attributesToHighlight.push({
            key: 'Monthly contribution limit',
            previousValue: monthlyProgress.total - monthlyProgress.current,
            currentValue: monthlyProgress.current,
            limit: monthlyContributionMaxCents,
            monthlyProgress,
        });
    }
    if (yearlyContributionMaxCents != null) {
        attributesToHighlight.push({
            key: 'Annual contribution limit',
            previousValue: yearlyProgress.total - yearlyProgress.current,
            currentValue: yearlyProgress.current,
            limit: yearlyContributionMaxCents,
            yearlyProgress,
        });
    }
    if (retirementContributionLimit != null) {
        attributesToHighlight.push({
            key: 'Annual IRA contribution limit',
            previousValue:
                retirementProgress.total - retirementProgress.current,
            currentValue: retirementProgress.current,
            limit: retirementContributionLimit,
            retirementProgress,
        });
    }
    if (targetBalanceCents != null) {
        attributesToHighlight.push({
            key: 'Target balance',
            previousValue: targetProgress.total - targetProgress.current,
            currentValue: targetProgress.total,
            limit: targetBalanceCents,
            targetProgress,
        });
    }

    if (attributesToHighlight.length === 0) return;

    const maxAttribute = attributesToHighlight.reduce((max, curr) =>
        curr.currentValue + curr.previousValue >
        max.currentValue + max.previousValue
            ? curr
            : max,
    );
    if (maxAttribute.key === 'Target balance') {
        maxAttribute.currentValue = targetProgress.current;
    }
    return maxAttribute;
};

const getPercentageLabel = (percentage?: number) => {
    if (percentage == null) return null;
    const normalizedPercentage =
        percentage <= 1 ? Math.round(percentage * 100) : Math.round(percentage);
    return `(${normalizedPercentage}%)`;
};

const formatTransferDate = (createdAt: string) => {
    return new Date(createdAt).toLocaleString('default', {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
    });
};

interface LimitMetInfo {
    createdAt: string | null;
    kind: GoalCompletionKind | null;
}

/**
 * Target balance: only when this execution's transfer crosses the threshold
 * (was below target before, at/after after). Avoids showing a past "met on" date
 * when balance later dipped and this execution did not re-reach target.
 *
 * If the rule also has monthly/yearly caps, those are evaluated when target did
 * not cross on this transfer (so e.g. "Monthly contribution limit met on" still
 * shows when the monthly cap was hit).
 *
 * Priority when multiple apply on the same transfer: target first, then monthly,
 * then annual.
 */
const getLimitMetInfo = (
    transfers: IncomeSplitTransfer[],
    rule: IncomeSplitRuleWithMoneyMapRule,
    referenceDate: string,
    currentTransfer: IncomeSplitTransfer,
): LimitMetInfo => {
    const sortedTransfers = [...transfers].sort(
        (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );

    if (rule.targetBalanceCents != null) {
        const target = rule.targetBalanceCents;
        const after = currentTransfer.balanceAfterTransaction;
        const before = after - currentTransfer.amount;
        if (after >= target && before < target) {
            return {
                createdAt: currentTransfer.createdAt,
                kind: 'target_balance',
            };
        }
    }

    const reference = new Date(referenceDate);

    if (rule.monthlyContributionMaxCents != null) {
        const monthlyTotals = new Map<string, number>();
        for (const transfer of sortedTransfers) {
            const transferDate = new Date(transfer.createdAt);
            if (
                transferDate.getFullYear() !== reference.getFullYear() ||
                transferDate.getMonth() !== reference.getMonth()
            ) {
                continue;
            }
            const monthKey = `${transferDate.getFullYear()}-${transferDate.getMonth()}`;
            const nextTotal =
                (monthlyTotals.get(monthKey) ?? 0) + transfer.amount;
            monthlyTotals.set(monthKey, nextTotal);
            if (nextTotal >= rule.monthlyContributionMaxCents) {
                return {
                    createdAt: transfer.createdAt,
                    kind: 'monthly_contribution',
                };
            }
        }
    }

    if (rule.yearlyContributionMaxCents != null) {
        const yearlyTotals = new Map<string, number>();
        for (const transfer of sortedTransfers) {
            const transferDate = new Date(transfer.createdAt);
            if (transferDate.getFullYear() !== reference.getFullYear()) {
                continue;
            }
            const yearKey = `${transferDate.getFullYear()}`;
            const nextTotal =
                (yearlyTotals.get(yearKey) ?? 0) + transfer.amount;
            yearlyTotals.set(yearKey, nextTotal);
            if (nextTotal >= rule.yearlyContributionMaxCents) {
                return {
                    createdAt: transfer.createdAt,
                    kind: 'annual_contribution',
                };
            }
        }
    }

    return { createdAt: null, kind: null };
};

const goalCompletionLeadIn = (
    kind: GoalCompletionKind | null | undefined,
): string => {
    switch (kind) {
        case 'target_balance':
            return 'Target balance met on';
        case 'monthly_contribution':
            return 'Monthly contribution limit met on';
        case 'annual_contribution':
            return 'Annual contribution limit met on';
        default:
            return 'Goal completed on';
    }
};

const IncomeSplitExecutionRuleSetTransfer = ({
    transfer,
    showTransfers,
}: {
    transfer: ExecutionRuleSetTransfer;
    showTransfers: boolean;
}) => {
    const {
        amount,
        destinationAccountDisplayName,
        destinationAccountInstitutionName,
        destinationAccountLogo,
        destinationAccountImgBase64,
        destinationAccountNumberMasked,
        destinationAccountIcon,
        destinationAccountColor,
        matchingTransfer,
        rule,
        previousTransfersForYear,
        createdAt,
        balanceAfterTransaction,
        goalCompletedAt,
        goalCompletionKind,
    } = transfer;
    const {
        monthlyContributionMaxCents,
        yearlyContributionMaxCents,
        targetBalanceCents,
        retirementContributionLimit,
        moneyMapRule,
    } = rule as IncomeSplitRuleWithMoneyMapRule;
    const { getColor } = useThemeColors();
    const resolveColor = (color: string) =>
        color.startsWith('$') ? getColor(color) : color;
    const { type: moneyMapRuleType } = moneyMapRule ?? {};
    const isExternal =
        rule.accountType === PaycheckRoutingRuleAccountType.EXTERNAL;
    const destinationAccountDetails =
        matchingTransfer?.transferPlan?.destinationAccountDetails ??
        matchingTransfer?.transferPlan?.destinationDepositoryAccountDetails ??
        matchingTransfer?.transferPlan?.destinationInvestmentAccountDetails;
    const resolvedDestinationAccountNumberMasked =
        destinationAccountNumberMasked ??
        destinationAccountDetails?.accountNumberMasked ??
        destinationAccountDetails?.accountNumber;
    const resolvedDestinationAccountLogo =
        destinationAccountLogo ?? destinationAccountDetails?.logo;
    const resolvedDestinationAccountImgBase64 =
        destinationAccountImgBase64 ?? destinationAccountDetails?.imgBase64;
    const resolvedDestinationAccountInstitutionName =
        destinationAccountInstitutionName ??
        destinationAccountDetails?.institutionName;
    const formattedDestinationAccountDisplayName = isExternal
        ? formatAccountNameWithLastFour(
              destinationAccountDisplayName,
              resolvedDestinationAccountNumberMasked,
          )
        : destinationAccountDisplayName;
    const accountIcon =
        destinationAccountIcon ??
        destinationAccountDetails?.icon ??
        rule.accountMetadata?.icon;
    const accountColor =
        destinationAccountColor ??
        destinationAccountDetails?.color ??
        rule.accountMetadata?.color;

    const hasTargetBalanceOrLimit = useMemo(
        () =>
            rule.monthlyContributionMaxCents != null ||
            rule.yearlyContributionMaxCents != null ||
            rule.targetBalanceCents != null ||
            rule.retirementContributionLimit != null,
        [rule],
    );

    const previousTransfersForMonth = useMemo(
        () =>
            previousTransfersForYear?.filter(
                (prevTransfer) =>
                    new Date(prevTransfer.createdAt).getMonth() ===
                    new Date(createdAt).getMonth(),
            ) ?? [],
        [previousTransfersForYear, createdAt],
    );

    const monthlyProgress = useMemo(
        () =>
            getProgress(
                amount,
                monthlyContributionMaxCents,
                previousTransfersForMonth,
            ),
        [previousTransfersForMonth, monthlyContributionMaxCents],
    );

    const yearlyProgress = useMemo(
        () =>
            getProgress(
                amount,
                yearlyContributionMaxCents,
                previousTransfersForYear ?? [],
            ),
        [previousTransfersForYear, yearlyContributionMaxCents, amount],
    );

    const retirementProgress = useMemo(
        () =>
            getProgress(
                amount,
                retirementContributionLimit,
                previousTransfersForYear ?? [],
            ),
        [previousTransfersForYear, retirementContributionLimit, amount],
    );

    const targetBalanceProgress = useMemo<Progress>(() => {
        return targetBalanceCents != null
            ? {
                  total: balanceAfterTransaction / targetBalanceCents,
                  current: amount / targetBalanceCents,
              }
            : { total: 0, current: 0 };
    }, [balanceAfterTransaction, targetBalanceCents]);

    /** Matches getLimitMetInfo target branch: threshold crossed on this transfer only. */
    const targetMetOnThisExecution = useMemo(() => {
        if (targetBalanceCents == null) return false;
        const after = balanceAfterTransaction;
        const before = after - amount;
        return after >= targetBalanceCents && before < targetBalanceCents;
    }, [targetBalanceCents, balanceAfterTransaction, amount]);

    const attributeToHighlight = useMemo(() => {
        return hasTargetBalanceOrLimit
            ? getAttributeToHighlight(
                  monthlyProgress,
                  monthlyContributionMaxCents,
                  yearlyProgress,
                  yearlyContributionMaxCents,
                  retirementProgress,
                  retirementContributionLimit,
                  targetBalanceProgress,
                  targetBalanceCents,
              )
            : undefined;
    }, [
        monthlyProgress,
        monthlyContributionMaxCents,
        yearlyProgress,
        yearlyContributionMaxCents,
        retirementProgress,
        retirementContributionLimit,
        targetBalanceProgress,
        targetBalanceCents,
    ]);

    const {
        previousValue: previousProgress,
        currentValue: currentProgress,
        key: progressType,
    } = attributeToHighlight ?? {};

    const limitMet = useMemo(
        () => (currentProgress ?? 0) + (previousProgress ?? 0) >= 1,
        [currentProgress, previousProgress],
    );

    const [trackWidth, setTrackWidth] = useState<number | null>(null);
    const progressWidth = useSharedValue(0);
    const initialWidthAnimationDoneRef = useRef(false);
    const hasPreviousSegment = (previousProgress ?? 0) > 0;
    const targetWidthPx =
        trackWidth != null
            ? (trackWidth - (hasPreviousSegment ? PROGRESS_BAR_GAP : 0)) *
              (currentProgress ?? 0)
            : 0;

    useEffect(() => {
        if (Platform.OS !== 'web' || trackWidth == null) return;
        if (!initialWidthAnimationDoneRef.current) {
            progressWidth.value = withTiming(targetWidthPx, {
                duration: PROGRESS_BAR_ENTERING_DURATION,
            });
            initialWidthAnimationDoneRef.current = true;
        } else {
            progressWidth.value = targetWidthPx;
        }
    }, [Platform.OS, trackWidth, targetWidthPx, progressWidth]);

    const webSegmentAnimatedStyle = useAnimatedStyle(() => ({
        width: progressWidth.value,
        borderRadius: PROGRESS_BAR_BORDER_RADIUS,
        overflow: 'hidden' as const,
    }));

    const percentageLabel = showTransfers
        ? getPercentageLabel(rule.percentage)
        : null;

    const transfersForMonth = useMemo(() => {
        const allTransfersForMonth = [...previousTransfersForMonth, transfer]
            .filter((monthTransfer) => monthTransfer.amount > 0)
            .filter((monthTransfer) =>
                goalCompletedAt != null
                    ? new Date(monthTransfer.createdAt) <=
                      new Date(goalCompletedAt)
                    : true,
            );
        return allTransfersForMonth.sort(
            (a, b) =>
                new Date(a.createdAt).getTime() -
                new Date(b.createdAt).getTime(),
        );
    }, [previousTransfersForMonth, transfer, goalCompletedAt]);

    const limitRows = [
        monthlyContributionMaxCents != null
            ? {
                  label: 'Monthly contribution limit',
                  amount: monthlyContributionMaxCents,
              }
            : null,
        yearlyContributionMaxCents != null
            ? {
                  label: 'Annual contribution limit',
                  amount: yearlyContributionMaxCents,
              }
            : null,
        retirementContributionLimit != null
            ? {
                  label: 'Annual IRA contribution limit',
                  amount: retirementContributionLimit,
              }
            : null,
        targetBalanceCents != null
            ? {
                  label: 'Target balance',
                  amount: targetBalanceCents,
              }
            : null,
    ].filter((row): row is { label: string; amount: number } => row != null);

    return (
        <YStack gap={12}>
            <XStack gap={12} alignItems="center" justifyContent="space-between">
                <XStack
                    gap={12}
                    alignItems="center"
                    justifyContent="space-between"
                >
                    {isExternal ? (
                        <InstitutionLogo
                            size="xxs"
                            logo={getExternalInstitutionLogo(
                                resolvedDestinationAccountLogo,
                                resolvedDestinationAccountImgBase64,
                            )}
                        />
                    ) : (
                        <FruitfulCustomIcon
                            icon={accountIcon}
                            color={accountColor}
                            size="xxs"
                            useAltColors={
                                rule.accountType ===
                                PaycheckRoutingRuleAccountType.INTERNAL_INVESTMENT
                            }
                        />
                    )}
                    <XStack gap={8} alignItems="center" flexShrink={1}>
                        <YStack gap={4}>
                            <XStack gap={8} alignItems="center" flexShrink={1}>
                                <BodyMediumRegular>
                                    {formattedDestinationAccountDisplayName}
                                </BodyMediumRegular>
                                {percentageLabel != null && (
                                    <Animated.View
                                        entering={FadeIn.duration(250)}
                                        exiting={FadeOut.duration(50)}
                                    >
                                        <BodySmallRegular color="$gray1000">
                                            {percentageLabel}
                                        </BodySmallRegular>
                                    </Animated.View>
                                )}
                            </XStack>
                            {isExternal &&
                                resolvedDestinationAccountInstitutionName !=
                                    null && (
                                    <BodySmallRegular color="$gray1000">
                                        {
                                            resolvedDestinationAccountInstitutionName
                                        }
                                    </BodySmallRegular>
                                )}
                        </YStack>
                    </XStack>
                </XStack>
                {transfer.status === 'FAIL' || transfer.status === 'FAILED' ? (
                    <BadgeLabel
                        variant="warning"
                        text={centsToFormattedDollars(amount)}
                        icon={AlertTriangleIcon}
                        size="regular"
                    />
                ) : isInternalInvestmentTransferPending(transfer) &&
                  rule.accountType ===
                      PaycheckRoutingRuleAccountType.INTERNAL_INVESTMENT ? (
                    <XStack gap={6} alignItems="center">
                        <BodyMediumRegular color="$gray800">
                            Pending
                        </BodyMediumRegular>
                        <BodyMediumRegular>
                            {centsToFormattedDollars(amount)}
                        </BodyMediumRegular>
                        <TooltipTrigger tooltipContent="Transfers to Fruitful investment accounts may take 1-2 business days to process.">
                            <Icon icon={InfoIcon} size={18} />
                        </TooltipTrigger>
                    </XStack>
                ) : (
                    <BodyMediumRegular>
                        +{centsToFormattedDollars(amount)}
                    </BodyMediumRegular>
                )}
            </XStack>
            {hasTargetBalanceOrLimit && (
                <YStack gap={8}>
                    <XStack
                        width="100%"
                        height={12}
                        borderRadius={4}
                        backgroundColor="$gray200"
                        gap={PROGRESS_BAR_GAP}
                        overflow="hidden"
                        onLayout={(e) => {
                            setTrackWidth(e.nativeEvent.layout.width);
                        }}
                    >
                        {(previousProgress ?? 0) > 0 && (
                            <View
                                borderRadius={4}
                                height={12}
                                width={`${(previousProgress ?? 0) * 100}%`}
                                backgroundColor={
                                    moneyMapRuleType != null
                                        ? IncomeSplitOverviewColors[
                                              moneyMapRuleType
                                          ].backgroundPrevious
                                        : '$peachLight'
                                }
                            />
                        )}
                        <Animated.View
                            entering={
                                Platform.OS === 'web'
                                    ? undefined
                                    : progressSegmentEntering
                            }
                            style={[
                                Platform.OS === 'web'
                                    ? webSegmentAnimatedStyle
                                    : null,
                                {
                                    overflow: 'hidden',
                                    borderRadius: PROGRESS_BAR_BORDER_RADIUS,
                                    height: 12,
                                    ...(Platform.OS !== 'web' && {
                                        width: `${(currentProgress ?? 0) * 100}%`,
                                    }),
                                    ...(Platform.OS === 'web' && {
                                        backgroundColor: resolveColor(
                                            moneyMapRuleType != null
                                                ? IncomeSplitOverviewColors[
                                                      moneyMapRuleType
                                                  ].background
                                                : '$peach',
                                        ),
                                    }),
                                },
                            ]}
                        >
                            {Platform.OS !== 'web' && (
                                <View
                                    style={{
                                        width: '100%',
                                        height: 12,
                                        borderRadius:
                                            PROGRESS_BAR_BORDER_RADIUS,
                                        backgroundColor: resolveColor(
                                            moneyMapRuleType != null
                                                ? IncomeSplitOverviewColors[
                                                      moneyMapRuleType
                                                  ].background
                                                : '$peach',
                                        ),
                                    }}
                                />
                            )}
                        </Animated.View>
                    </XStack>

                    {limitRows.length > 0 ? (
                        <YStack gap={8}>
                            {limitRows.map((row) => {
                                const isTargetBalance =
                                    row.label === 'Target balance';
                                const shouldShowCompletion =
                                    row.label === 'Target balance'
                                        ? targetMetOnThisExecution
                                        : row.label ===
                                            'Monthly contribution limit'
                                          ? goalCompletionKind ===
                                            'monthly_contribution'
                                          : row.label ===
                                              'Annual contribution limit'
                                            ? goalCompletionKind ===
                                              'annual_contribution'
                                            : false;
                                const isLimitMet =
                                    row.label === progressType && limitMet;

                                return (
                                    <XStack
                                        key={row.label}
                                        alignItems="center"
                                        justifyContent="space-between"
                                        gap={12}
                                    >
                                        <BodySmallRegular color="$gray1000">
                                            {row.label}
                                        </BodySmallRegular>
                                        <XStack gap={4} alignItems="center">
                                            <BodySmallRegular
                                                color={
                                                    (isTargetBalance &&
                                                        shouldShowCompletion) ||
                                                    isLimitMet
                                                        ? '$green1000'
                                                        : '$gray1000'
                                                }
                                            >
                                                {centsToFormattedDollars(
                                                    row.amount,
                                                    false,
                                                )}
                                            </BodySmallRegular>
                                            {((isTargetBalance &&
                                                shouldShowCompletion) ||
                                                isLimitMet) && (
                                                <Icon
                                                    icon={CircleCheckIcon}
                                                    color="$green1000"
                                                    size={16}
                                                />
                                            )}
                                        </XStack>
                                    </XStack>
                                );
                            })}
                        </YStack>
                    ) : null}
                </YStack>
            )}
            {showTransfers && (
                <Animated.View
                    entering={FadeIn.duration(250)}
                    exiting={FadeOut.duration(50)}
                >
                    <YStack gap={4}>
                        {goalCompletedAt != null && (
                            <BodySmallRegular color="$gray1000">
                                {goalCompletionLeadIn(goalCompletionKind)}{' '}
                                {formatTransferDate(goalCompletedAt)}
                            </BodySmallRegular>
                        )}
                        {transfersForMonth.map((monthTransfer) => (
                            <XStack
                                key={`${monthTransfer.id}-${monthTransfer.createdAt}`}
                                alignItems="center"
                                justifyContent="space-between"
                                gap={12}
                            >
                                <BodySmallRegular color="$gray1000">
                                    {' '}
                                    &#8226;{' '}
                                    {formatTransferDate(
                                        monthTransfer.createdAt,
                                    )}
                                </BodySmallRegular>
                                <BodySmallRegular color="$gray1000">
                                    +
                                    {centsToFormattedDollars(
                                        monthTransfer.amount,
                                    )}
                                </BodySmallRegular>
                            </XStack>
                        ))}
                    </YStack>
                </Animated.View>
            )}
        </YStack>
    );
};

const IncomeSplitExecutionRuleSet = ({
    result,
    onExpandRuleSet,
}: {
    result: RuleSetTransfersResult;
    onExpandRuleSet?: (ruleSetName: string) => void;
}) => {
    const { name, transfers } = result;
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <Animated.View
            entering={FadeIn.duration(250)}
            exiting={FadeOut.duration(250)}
        >
            <YStack borderRadius={24} overflow="hidden">
                <XStack
                    gap={12}
                    padding={16}
                    alignItems="center"
                    justifyContent="space-between"
                    backgroundColor="$gray200"
                >
                    <BodyMediumRegular>{name}</BodyMediumRegular>
                    <View
                        padding={4}
                        cursor="pointer"
                        onPress={() => {
                            if (!isExpanded) {
                                onExpandRuleSet?.(name);
                            }
                            setIsExpanded((prev) => !prev);
                        }}
                    >
                        <Icon
                            icon={isExpanded ? Minimize2Icon : Maximize2Icon}
                            size={24}
                            color="$gray1200"
                        />
                    </View>
                </XStack>
                <YStack backgroundColor="#FCFCFC" padding={16}>
                    {transfers.map((transfer, index) => (
                        <YStack
                            key={
                                transfer.originalRuleId !== ''
                                    ? transfer.originalRuleId
                                    : transfer.ruleId
                            }
                            marginTop={index > 0 ? 16 : 0}
                        >
                            <IncomeSplitExecutionRuleSetTransfer
                                transfer={transfer}
                                showTransfers={isExpanded}
                            />
                        </YStack>
                    ))}
                </YStack>
            </YStack>
        </Animated.View>
    );
};

export const IncomeSplitExecutionRuleSets = ({
    incomeSplitOverview,
    onExpandRuleSet,
    execution: executionProp,
}: {
    incomeSplitOverview: IncomeSplitOverviewData | null;
    onExpandRuleSet?: (ruleSetName: string) => void;
    /**
     * Execution to render the breakdown for. Takes precedence over the global
     * selection atom so the component can be reused outside the overview page
     * (e.g. the Sprout chat widget) without depending on shared state.
     */
    execution?: IncomeSplitExecution;
}) => {
    const selectedExecution = useAtomValue(selectedIncomeSplitExecutionAtom);
    const execution = executionProp ?? selectedExecution;

    if (!incomeSplitOverview || !execution) {
        return null;
    }

    const { configId, transfers: executionTransfers } = execution;

    const config = incomeSplitOverview.configs.find(
        (config) => config.id === configId,
    );
    if (!config) {
        return null;
    }

    const { ruleSets } = config;

    const allTransfers = incomeSplitOverview.history
        .map((month) =>
            month.executions.map((execution) => execution.transfers).flat(),
        )
        .flat();

    const ruleSetTransfersResults = ruleSets.map<RuleSetTransfersResult>(
        (ruleSet, index) => {
            const { name, rules } = ruleSet;

            const group = {
                name: name ?? `Rule set ${index}`,
            };

            const transfers = rules
                .map((rule) => {
                    const { id, originalId } = rule;

                    const transfer = executionTransfers.find(
                        (transfer) =>
                            transfer.ruleId === id ||
                            transfer.originalRuleId === originalId,
                    );
                    if (!transfer) {
                        return null;
                    }

                    const previousTransfersForYear = allTransfers.filter(
                        (prevTransfer) => {
                            const currentTransferDate = new Date(
                                transfer.createdAt,
                            );
                            const prevTransferDate = new Date(
                                prevTransfer.createdAt,
                            );

                            return (
                                prevTransfer.id !== transfer.id &&
                                prevTransferDate.getFullYear() ===
                                    currentTransferDate.getFullYear() &&
                                prevTransferDate < currentTransferDate &&
                                (prevTransfer.ruleId === id ||
                                    prevTransfer.ruleId === originalId ||
                                    prevTransfer.originalRuleId === originalId)
                            );
                        },
                    );

                    const ruleTransfers = allTransfers.filter(
                        (prevTransfer) =>
                            prevTransfer.ruleId === id ||
                            prevTransfer.ruleId === originalId ||
                            prevTransfer.originalRuleId === originalId,
                    );

                    const limitMetInfo = getLimitMetInfo(
                        ruleTransfers,
                        rule as IncomeSplitRuleWithMoneyMapRule,
                        transfer.createdAt,
                        transfer,
                    );

                    const executionRuleSetTransfer: ExecutionRuleSetTransfer = {
                        ...transfer,
                        rule,
                        previousTransfersForYear,
                        goalCompletedAt: limitMetInfo.createdAt,
                        goalCompletionKind: limitMetInfo.kind,
                    };
                    return executionRuleSetTransfer;
                })
                .filter(
                    (transfer): transfer is ExecutionRuleSetTransfer =>
                        transfer != null,
                );

            return {
                ...group,
                transfers: [...transfers].sort((a, b) => {
                    const aHasLimit =
                        a.rule.monthlyContributionMaxCents != null ||
                        a.rule.yearlyContributionMaxCents != null ||
                        a.rule.targetBalanceCents != null;
                    const bHasLimit =
                        b.rule.monthlyContributionMaxCents != null ||
                        b.rule.yearlyContributionMaxCents != null ||
                        b.rule.targetBalanceCents != null;

                    if (aHasLimit !== bHasLimit) {
                        return aHasLimit ? -1 : 1;
                    }

                    return a.destinationAccountDisplayName.localeCompare(
                        b.destinationAccountDisplayName,
                    );
                }),
            };
        },
        {},
    );

    return (
        <YStack gap={16}>
            {ruleSetTransfersResults
                .filter(
                    (ruleSetTransferResult) =>
                        ruleSetTransferResult.transfers.length,
                )
                .map((ruleSetTransfersResult) => (
                    <IncomeSplitExecutionRuleSet
                        key={ruleSetTransfersResult.name}
                        result={ruleSetTransfersResult}
                        onExpandRuleSet={onExpandRuleSet}
                    />
                ))}
        </YStack>
    );
};
